/**
 * createARoom.js
 *
 * Basic function to create a room for a game
 */

import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../config/firebase";
import { fetchRoomDetails } from "./joinRoom";

// No 0/O or 1/I/L, so codes are easy to read out loud
const ROOM_CODE_CHARACTERS = "abcdefghjkmnpqrstuvwxyz23456789";
const ROOM_CODE_LENGTH = 6;
const MAX_ATTEMPTS = 5;

/**
 * Generates a random lowercase room code (rooms are case-insensitive, stored lowercase)
 * @returns {String}    -   e.g. 'k7mq2x'
 */
export const generateRoomCode = () => {
    let code = "";
    for (let i = 0; i < ROOM_CODE_LENGTH; i++)
        code += ROOM_CODE_CHARACTERS[Math.floor(Math.random() * ROOM_CODE_CHARACTERS.length)];
    return code;
};

/**
 * createARoom
 *
 * Creates a new Game Room in Firestore with a generated room code,
 * and makes the user the admin (host) of that room.
 *
 * @param {object} user - The user creating the room (must have displayName and uid).
 * @param {string} dbCollection - The Firestore collection name (e.g. 'AuctionRooms').
 * @returns {object|null} - Returns the created room document (with ID and data) or null on failure.
 */
export const createARoom = async (user, dbCollection) => {
    try {
        // Pick a code that isn't already taken
        let roomID = null;
        for (let attempt = 0; attempt < MAX_ATTEMPTS && !roomID; attempt++) {
            const code = generateRoomCode();
            const existingRoom = await fetchRoomDetails(dbCollection, code);
            if (!existingRoom) roomID = code;
        }
        if (!roomID) return null;

        const roomRef = doc(db, dbCollection, roomID);

        // --- Construct the room data ---
        const roomData = {
            admin           :   {
                displayName :   user.displayName || "",
                userID      :   user.uid || user.userID || "",
            },
            createdAt       :   Timestamp.now(),
        };

        // --- Add the document and get reference ---
        await setDoc(roomRef, roomData);

        // --- Return full document including ID ---
        return {
            id: roomID,
            ...roomData
        };
    } catch (error) {
        console.error("Error creating a room:", error);
        return null;
    }
};
